import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { alpha, styled, useTheme } from '@mui/material/styles';

import {
    Box,
    List,
    Link,
    Drawer,
    Collapse,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    Divider,
    Grid,
} from '@mui/material';
// config
import { NAVBAR } from '../config';
// components
import Logo from '../components/Logo';
import Iconify from '../components/Iconify';
import Scrollbar from '../components/Scrollbar';
import { IconButtonAnimate } from '../components/animate';
import { NavSectionVertical } from '../components/nav-section';
import { ICON_SIZE } from './MenuConfig';
import useResponsive from '../hooks/useResponsive';
import typography from '../theme/typography';

// ----------------------------------------------------------------------

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
    ...theme.typography.body2,
    height: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
    textTransform: 'capitalize',
    color: theme.palette.text.secondary,
}));

// ----------------------------------------------------------------------

MenuMobile.propTypes = {
    isOffset: PropTypes.bool,
    isHome: PropTypes.bool,
    navConfig: PropTypes.array,
};

export default function MenuMobile({ isOffset, isHome, navConfig, user, languages, showLanguageSwitcher, rootPath }) {
    const { pathname } = useLocation();
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const isDesktop = useResponsive('up', 'md');
    const isMobile = useResponsive('down', 'sm');
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        if (drawerOpen) {
            handleDrawerClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const handleOpen = () => {
        setOpen(!open);
    };

    const handleDrawerOpen = () => {
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    return (
        <>
            <IconButtonAnimate
                onClick={handleDrawerOpen}
                sx={{
                    ml: 1,
                    ...(isHome && { color: 'common.white' }),
                    ...(isOffset && { color: 'text.primary' }),
                }}
            >
                <Iconify icon={'eva:menu-2-fill'} />
            </IconButtonAnimate>

            <Drawer
                open={drawerOpen}
                onClose={handleDrawerClose}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { pb: 5, width: 260 } }}
            >
                <Scrollbar>
                    <Link
                        href="/"
                        sx={{
                            ...typography.logo,
                            my: 3,
                            color: theme.palette.grey[700],
                            fontSize: isMobile ? 15 : 20,
                        }}
                        underline="none"
                    >
                        <Logo
                            disabledLink={true}
                            sx={{ mx: 2.5, mb: 3, display: 'inline-block', verticalAlign: 'middle', mt: 2.7 }}
                        />
                        WP BLAST
                    </Link>

                    <List disablePadding>
                        {navConfig.map((link) => (
                            <MenuMobileItem key={link.title} item={link} isOpen={open} onOpen={handleOpen} />
                        ))}
                        {!isDesktop && !user ? (
                            <MenuMobileItem
                                key={'Register'}
                                item={{ title: 'Register', url: rootPath + 'register/', attr_title: 'mdi:account' }}
                                isOpen={open}
                                onOpen={handleOpen}
                            />
                        ) : null}
                        {languages && !showLanguageSwitcher && (
                            <>
                                <Divider
                                    variant="middle"
                                    sx={{
                                        marginTop: '20px',
                                        marginBottom: '20px',
                                    }}
                                />
                                <Grid container spacing={0} alignItems="center" justifyContent="center" gap="20px">
                                    {Object.values(languages).map((language) => (
                                        <Link key={language.id} href={language.url}>
                                            <Iconify
                                                icon={language.code === 'fr' ? 'cif:fr' : 'cif:us'}
                                                sx={{
                                                    width: 25,
                                                    height: 25,
                                                    verticalAlign: 'middle',
                                                    transition: 'opacity 0.3s',
                                                    opacity: 1,
                                                    '&:hover': { opacity: '0.5' },
                                                }}
                                            />
                                        </Link>
                                    ))}
                                </Grid>
                            </>
                        )}
                    </List>
                </Scrollbar>
            </Drawer>
        </>
    );
}

// ----------------------------------------------------------------------

MenuMobileItem.propTypes = {
    isOpen: PropTypes.bool,
    item: PropTypes.shape({
        child: PropTypes.array,
        attr_title: PropTypes.any,
        url: PropTypes.string,
        title: PropTypes.string,
    }),
    onOpen: PropTypes.func,
};

function MenuMobileItem({ item, isOpen, onOpen }) {
    const { title, url, attr_title, child } = item;

    if (child) {
        return (
            <>
                <ListItemStyle onClick={onOpen}>
                    {attr_title ? (
                        <ListItemIcon>
                            <Iconify icon={attr_title} {...ICON_SIZE} />
                        </ListItemIcon>
                    ) : null}
                    <ListItemText disableTypography primary={<span dangerouslySetInnerHTML={{ __html: title }} />} />
                    <Iconify
                        icon={isOpen ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
                        sx={{ width: 16, height: 16, ml: 1 }}
                    />
                </ListItemStyle>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ display: 'flex', flexDirection: 'column-reverse' }}>
                        <NavSectionVertical navConfig={child} />
                    </Box>
                </Collapse>
            </>
        );
    }

    return (
        <ListItemStyle
            //to={url}
            //component={RouterLink}
            //end={url === '/'}
            href={url}
            component={Link}
            sx={{
                '&.active': {
                    color: 'primary.main',
                    fontWeight: 'fontWeightMedium',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                },
            }}
        >
            {attr_title ? (
                <ListItemIcon>
                    <Iconify icon={attr_title} {...ICON_SIZE} />
                </ListItemIcon>
            ) : null}
            <ListItemText disableTypography primary={<span dangerouslySetInnerHTML={{ __html: title }} />} />
        </ListItemStyle>
    );
}
